<template>
	<div id="request-modal-wrapper" class="wrapper">
		<div class="wrapper-header">
			<div class="button-wrapper">
				<div class="left-side">
					<md-button class="md-icon-button" @click="backButton">
						<base-icon class="back-icon" icon-name="arrow-back" />
					</md-button>
					<div class="mobile-hide">
						<p class="H5-5">{{ headerTitle }}</p>
					</div>
				</div>

				<div class="right-side">
					<md-button
						id="next-step-button"
						v-if="currentStep < 2"
						class="md-primary md-button-large md-raised apply-btn"
						@click="goToNextStep"
						type="submit"
					>
						{{ buttonText }}
					</md-button>
				</div>
			</div>
		</div>
		<div class="request-container">
			<transition name="banner-fade" mode="out-in">
				<banner
					:content="getBannerContent"
					v-show="rqBannerVisable"
					@closeBanner="closeRQModalBanner"
					key="rqBannerVisable"
					ref="rqModalBanner"
				/>
			</transition>
			<div v-if="genomic && currentStep < 1" class="dropdown-wrapper">
				<div class="request-container-genomic">
					<p class="H5-5">Number of samples:</p>
					<div class="request-container-genomic-dropdown">
						<div class="number-of-samples-container">
							<div class="quantity-input">
								<dropdown-custom
									:placeholder="'Number of samples'"
									:list-options="numberOfSamplesOptions"
									:value="numberOfSamplesSelected"
									@input="setNumberOfSamplesSelected"
								/>
								<span v-if="numberOfSamplesValidation" class="md-error">{{
									numberOfSamplesValidation
								}}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="request-container-body">
				<keep-alive>
					<component
						:is="currentComponent || steps[0]"
						v-bind="$props"
						v-model="formData"
						@preventNextStep="preventNextStep"
						:ref="currentComponent"
						:loading="isLoading"
						@backToWorkflow="$emit('backButton')"
						:genomic="genomic"
					></component>
				</keep-alive>
				<div class="bottom-btn-row" :class="{'form-btn': currentStep === 1}">
					<md-button
						v-if="currentStep < 2"
						class="md-primary md-button-large md-raised apply-btn"
						@click="goToNextStep"
						type="submit"
					>
						{{ buttonText }}
					</md-button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {mapState} from 'vuex';
import {trackEvent} from '@/services/tracking.js';
import {createLanguageOption} from '@/util/createLanguageOption.js';
import ProductsView from '@/components/modals/requestsModalComponents/ProductsView.vue';
import RequestForm from '@/components/modals/requestsModalComponents/RequestForm.vue';
import SendingForm from '@/components/modals/requestsModalComponents/SendingForm.vue';
import callToApiBackend from '@/components/workflow/mixins/callToApiBackend';
import stepProductMethods from '@/components/workflow/mixins/stepProductMethods';
import intersection from '@/components/modals/mixins/intersection';
import DropdownCustom from '@/components/dropdown-custom/DropdownCustom';

export default {
	name: 'RequestsModal',
	props: {
		workflowSteps: {
			type: Array,
		},
		country: {
			type: String,
			require: true,
			default: 'US',
		},
		genomic: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		ProductsView,
		RequestForm,
		SendingForm,
		DropdownCustom,
	},
	mixins: [callToApiBackend, stepProductMethods, intersection],
	data() {
		return {
			currentStep: 0,
			steps: ['ProductsView', 'RequestForm', 'SendingForm'],
			formData: {},
			samplesQuantity: ['1-25', '26-100', '101-500', '> 500'],
			numberOfSamplesOptions: null,
			numberOfSamplesSelected: null,
			allUnselected: false,
			isLoading: true,
			productQuantity: null,
			selectedProducts: null,
			keepFormData: null,
			workflowStepsNames: [],
			rqBannerVisable: false,
			banner: {
				title: '',
				body: '',
				version: '',
				btn: '',
				btnEmits: '',
			},
			numberOfSamplesValidation: null,
		};
	},
	created() {
		this.$root.$on('showRQModalBanner', this.showRQModalBanner);
		this.$root.$on('closeRQModalBanner', this.closeRQModalBanner);
		this.$root.$emit('closeGlobalBanner');
		this.$root.$on('stepSelected', this.stepSelected);
		this.numberOfSamplesOptions = this.samplesQuantity.map((quantity) =>
			createLanguageOption(quantity, this.getLang),
		);
	},
	beforeDestroy() {
		this.$root.$emit('unsetGenomic');
		this.removeObserverHeader();
	},
	computed: {
		...mapState(['jwt', 'salesProspectEndpoint']),
		getLang() {
			return this.$store.state.LANG || 'enUS';
		},
		headerTitle() {
			return {
				0: this.genomic
					? 'Select the experiment steps you would like Genomic Services to process'
					: 'Select products to contact Sales',
				1: 'Complete personal information',
				2: 'Back to experiment',
			}[this.currentStep];
		},
		buttonText() {
			return {
				0: 'Next',
				1: this.genomic ? 'contact genomic services' : 'Contact Sales',
			}[this.currentStep];
		},
		currentComponent() {
			if (this.currentStep < 0) {
				this.$emit('backButton');
				return this.steps[this.currentStep];
			}
			return this.steps[this.currentStep];
		},
		salesProspectUrl() {
			return this.genomic ? 'genomic_service' : 'request_quote';
		},
		finalStepsString() {
			return `${this.workflowStepsNames
				.map((stepsName) => stepsName.workflowStepName + '\n')
				.join(' ')} Project size: ${this.numberOfSamplesSelected.value[this.getLang]}`;
		},
		getBannerContent() {
			return this.banner;
		},
		allStepsUnselected() {
			return this.workflowStepsNames?.length == 0
		}
	},
	mounted() {
		if (this.genomic) {
			this.workflowStepsNames = this.getWorkflowStepsNames();
		}
		this.$bus.$emit(this.$bus.CLEAR_SNACKBAR);
		this.addObserverHeader();
	},
	watch: {
		allUnselected(newVal) {
			if (!newVal) {
				this.closeRQModalBanner();
				this.clearSnack();
			}
		},
		allStepsUnselected(newVal) {
			if (!newVal) {
				this.closeRQModalBanner();
				this.clearSnack();
			} else {
				this.selectProductSnack();
			}
		},
	},
	methods: {
		setNumberOfSamplesSelected(option) {
			this.numberOfSamplesSelected = option;
			this.numberOfSamplesValidation = null;
		},

		getWorkflowStepsNames() {
			const result = this.workflowSteps
				.filter(
					(step) =>
						step.stepEnabled &&
						!this.isCustomWorkStep(step) &&
						!this.isGeneGlobeProduct(step) &&
						!this.isCustomProduct(step),
				)
				.map((workflowStep) => {
					const checkForName = this.$store.state.cvlWebSteps.values.find(
						(step) => step.key === workflowStep.name,
					);

					const stepName = checkForName ? checkForName.value[this.$store.state.LANG] : workflowStep.name;

					return {
						workflowStepKey: workflowStep.name,
						workflowStepName: stepName,
					};
				});

			return result;
		},

		stepSelected(selectedWorkflowStep) {
			const checkForName = this.$store.state.cvlWebSteps.values.find((step) => step.key === selectedWorkflowStep);

			const stepName = checkForName ? checkForName.value[this.$store.state.LANG] : selectedWorkflowStep;

			const workflowStepsKeys = this.workflowStepsNames.map((workflowStep) => workflowStep.workflowStepKey);
			if (workflowStepsKeys.includes(selectedWorkflowStep)) {
				this.workflowStepsNames = this.workflowStepsNames.filter(
					(workflowStep) => workflowStep.workflowStepKey !== selectedWorkflowStep,
				);
			} else {
				this.workflowStepsNames = [
					...this.workflowStepsNames,
					{workflowStepKey: selectedWorkflowStep, workflowStepName: stepName},
				];
			}
		},

		async goToNextStep() {
			if (this.genomic && !this.numberOfSamplesSelected) {
				this.sendTrack('next');
				return (this.numberOfSamplesValidation = 'Please select number of samples.');
			}
			this.numberOfSamplesValidation = null;

			// refactor
			if (this.genomic && this.allStepsUnselected) {
				this.selectProductSnack();
				return;
			} else if (this.allUnselected) {
					this.selectProductSnack();
					return;

			}
			if (this.currentStep === 0) {
				this.sendTrack('next');

				this.productQuantity = this.$refs[this.steps[this.currentStep]].productQuantity;
				this.selectedProducts = this.$refs[this.steps[this.currentStep]].selectedProducts;
				this.currentStep++;
			} else if (this.currentStep === 1) {
				this.sendTrack('form');

				this.keepFormData = this.$refs[this.steps[this.currentStep]].form;
				this.submitRequest();
			} else {
				this.currentStep++;
			}
			this.clearSnack();
		},
		sendTrack(suffix) {
			if (this.genomic) {
				trackEvent('ctaClick', {eVar58: `Contact Genomic ${suffix} | Workflow Configurator`});
			} else {
				trackEvent('ctaClick', {eVar58: `Contact sales ${suffix} | Workflow Configurator`});
			}
		},
		isFormValidated() {
			return this.$refs.RequestForm.validateForm();
		},
		clearSnack() {
			this.$bus.$emit(this.$bus.CLEAR_SNACKBAR);
		},
		preventNextStep(val) {
			this.allUnselected = !val;
			if (!val) {
				this.selectProductSnack();
			}
		},
		selectProductSnack() {
			if (this.genomic) {
				return this.$root.$emit('showRQModalBanner', {
					title: 'At least one step must be selected.',
					body: 'Please select at least one step to proceed.',
					version: 'warning',
				});
			}
			return this.$root.$emit('showRQModalBanner', {
				title: 'At least one product must be selected.',
				body: 'Please select at least one product to proceed.',
				version: 'warning',
			});
		},

		createProductQuantityString() {
			const filterSelected = (key) => this.selectedProducts[key];

			const removeStep = (key) => key.slice(0, key.lastIndexOf('-'));
			const createString = (current, [key, value]) => `${current}  ${removeStep(key)} qty ${value}  `;

			function stringifyProductQuantity(current, [key, value]) {
				return filterSelected(key) ? createString(current, [key, value]) : current;
			}

			return Object.entries(this.productQuantity).reduce(stringifyProductQuantity, '');
		},

		setFormData() {
			let genomicInterest;
			const productQuantityString = this.createProductQuantityString();
			let referralSource = window.location.href;
			if (referralSource.includes('?modal=open'))
				referralSource = referralSource.replace('?modal=open', '');

			const specificInterest = `A new request from workflow configuration. Link to workflow: ${referralSource}. Catalog numbers of interested products:${productQuantityString}`;
			if (this.genomic) {
				genomicInterest = `A new request from workflow configuration. Link to workflow: ${referralSource}. Steps in which are user interested: ${this.finalStepsString}`;
			}
			const validateCountry = () => {
				const getCountry = {
					US: 'USA',
					CA: 'Canada',
					default: this.formData.country.value,
				};
				return getCountry[this.formData.country.value] || getCountry.default;
			};
			return {
				'referral-source': referralSource,
				'specific-interest': this.genomic ? genomicInterest : specificInterest,
				'company': this.formData.company.value,
				'job-position': this.formData.jobPosition.value,
				'city': this.formData.city.value,
				'phone': this.formData.phone.value,
				'country': validateCountry(),
				'state-or-province': this.formData.state.value,
			};
		},
		async submitRequest() {
			const validate = await this.isFormValidated();
			if (validate) {
				const formData = this.setFormData();

				if (formData['specific-interest'].length >= 1000) {
					if (this.genomic) {
						return this.$snackbar({title: 'Selected steps max length 1000 characters.', btn: 'dismiss'});
					}
					return this.$snackbar({title: 'Selected products max length 1000 characters.', btn: 'dismiss'});
				}

				this.currentStep++;
				this.isLoading = true;
				const url = `${this.$store.state.salesProspectEndpoint}${this.salesProspectUrl}`;
				const data = JSON.stringify(formData);

				const response = await this.callToApi(url, 'post', data);

				if (!response) {
					this.currentStep = 1;
					this.$root.$emit('showRQModalBanner', {
						title: 'Query not sent.',
						body: 'Due to technical reasons, we were unable to send your query. Please try again.',
						version: 'error',
						dismissible: true,
					});
				} else {
					this.isLoading = false;
				}
			}
		},
		backButton() {
			this.clearSnack();
			if (this.currentStep === 2) {
				this.$emit('backButton');
			} else {
				this.currentStep--;
			}
		},
		showRQModalBanner(obj) {
			this.banner = obj;
			this.rqBannerVisable = true;
		},
		closeRQModalBanner() {
			this.rqBannerVisable = false;
		},
	},
};
</script>

<style lang="scss" scoped>
.request-container-body {
	-webkit-transition: padding-top 0.3s ease-out;
	-moz-transition: padding-top 0.3s ease-out;
	-o-transition: padding-top 0.3s ease-out;
	transition: padding-top 0.3s ease-out;
}

.request-container {
	.banner {
		position: sticky;
		width: 100%;
		z-index: 1100;
		box-shadow: $banner-shadow;
		top: 0px;
	}
}
.md-menu.md-select {
	font-size: 12px;
	@include font-styles('Futura W01 Book', 400);
}

.md-error {
	font-family: 'Futura W01 Book', Arial, Helvetica, sans-serif !important;
	font-size: 12px !important;
	padding-left: 10px;
	color: $color-red;
	transition: 0.3s;
}

.validation-error {
	border: 1px solid $color-red !important;
	box-shadow: none;
	transition: 0.3s;
}

.md-list-item,
.md-list-item-content,
.md-list-item-content.md-ripple {
	width: 200px;
}

.request-container-genomic-dropdown {
	height: 60px;
}

.number-of-samples-container {
	flex: 1;
	@include flexRow;
	align-items: flex-start;
	justify-content: space-between;

	.quantity-input {
		width: 228px;
		position: relative;

		@media (max-width: $mobile) {
			width: 100%;
		}

		.md-field {
			height: 40px;
			border-radius: 10px;
			padding: 6px 16px;
			border: 1px solid $color-grey;
			.md-menu,
			.md-menu .md-icon-image svg {
				fill: $color-text-grey;
			}
			&.md-focused {
				border: 2px solid $color-blue;
				transition: 0.3s;
				label {
					color: $color-blue;
				}
				&.not-selected {
					border: 1px solid $color-grey;
					transition: 0.3s;
					label {
						color: $color-grey;
					}
					.md-menu {
						color: $color-text-grey;
					}
				}
			}
			&.md-disabled {
				cursor: not-allowed;
			}
		}
		.md-menu.md-select {
			overflow: hidden;
			.md-input {
				height: 100%;
				width: 100%;
			}
		}
	}
}

.wrapper {
	@include box(100%, auto);
	position: fixed;
	top: 0;
	left: 0;
	height: 100vh; // fix scroll jump
	background: $white;
	z-index: 10001;
	overflow: hidden; // fix scroll jump
	.wrapper-header {
		position: fixed;
		top: 0;
		width: 100%;
		background: $white;
		z-index: 10000;
		box-shadow: 0px 2px 4px rgba(8, 35, 48, 0.24);

		.button-wrapper {
			padding: 12px 32px 12px 32px;

			@media (max-width: $desktop) {
				padding: 12px 16px 12px 10px;
			}

			margin: 0 auto;
			display: flex;
			flex-direction: row;
			justify-content: space-between;

			.md-button {
				margin: 0;
			}
		}
		.left-side {
			display: flex;
			align-items: center;
			padding-right: 10px;
			p {
				margin: 0;
				padding-left: 10px;
			}
		}
		.right-side {
			display: flex;
			justify-content: center;
			justify-items: center;
			flex-direction: column;
			button {
				max-height: 40px;
				@media (min-width: $mobile + 1) {
					.md-ripple {
						padding: 8px 16.5px;
					}
					.icons {
						margin-right: 1px;
					}
				}
			}
		}
	}
	.request-container {
		position: relative;
		height: 100vh; // fix scroll jump
		overflow: auto; // fix scroll jump
		padding-top: 65px;

		&-body {
			@include flexColumn;
			align-items: center;
			padding: 24px 10px 0;
			.bottom-btn-row {
				padding: 17px 0 15px 0;
				width: 100%;
				max-width: 1236px;
				@include flexRow;
				align-items: center;
				justify-content: flex-end;
				&.form-btn {
					max-width: 815px;
				}

				.apply-btn {
					@media (max-width: $mobile) {
						width: 100%;
						margin-top: 6px;
						margin-bottom: 26px;
					}
				}
			}

			@media (max-width: $mobile) {
				margin: 0 16px 16px 16px;
				padding: 24px 0 0;
				display: block;
			}
		}

		.dropdown-wrapper {
			padding: 0 16px;
			.request-container-genomic {
				max-width: 1236px;
				margin: 0 auto;
			}

			@media (max-width: $mobile) {
				padding: 0;
				width: inherit;
				margin: 0 16px;
			}
		}

		&-genomic {
			position: relative;
			margin: 20px 0;
			display: flex;
			flex-direction: row;
			align-items: center;
			padding-top: 24px;

			@media (max-width: $mobile) {
				flex-direction: column;
				align-items: flex-start;
				margin-bottom: 0;
			}

			p {
				margin-right: 15px;
			}

			&-dropdown {
				min-width: 200px;

				@media (max-width: $mobile) {
					width: 100%;
				}
			}
		}
	}
}
</style>
